import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {connect} from 'react-redux';

import type {Dispatch} from 'redux';
import {compose} from 'redux';
import {reduxForm, formValueSelector, SubmissionError} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';

import {ActivityTypes} from 'nutshell-core/activity-types';
import {Session} from 'nutshell-core/session';
import type {ActivityType} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {GoalType} from 'nutshell-graphql-types';
import type {
    ConditionInput,
    CreateGoal as CreateGoalMutation,
    CreateGoalVariables as CreateGoalMutationVariables,
    DeleteGoal as DeleteGoalMutation,
    DeleteGoalInputFieldInput,
    DeleteGoalVariables as DeleteGoalMutationVariables,
    EntityFilterFieldConfigInput,
    GetGoal as GetGoalQuery,
    GetGoalVariables as GetGoalQueryVariables,
    SalesAutomationStageGoal as SalesAutomationStageGoalFragment,
    StageGoalsForStage as StageGoalsForStageQuery,
    UpdateGoal as UpdateGoalMutation,
    UpdateGoalVariables as UpdateGoalMutationVariables,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../apollo-client';
import type {NutshellState} from '../../../../store';

import {refetchSalesAutomationOpenLeadQueries} from '../../app/sales-automation-open-leads';
import CREATE_GOAL from '../../graphql/mutations/create-goal.graphql';
import DELETE_GOAL from '../../graphql/mutations/delete-goal.graphql';
import UPDATE_GOAL from '../../graphql/mutations/update-goal.graphql';
import GET_GOAL from '../../graphql/queries/get-goal.graphql';
import GET_STAGE_GOALS_FOR_STAGE from '../../graphql/queries/get-stage-goals-for-stage.graphql';

import {isActivityTypeId, isNotActivityTypeId} from './activity-type';
import {FormGoalsComponent} from './form-goals-component';
import type {GoalsFormData} from './form-goals-component';

const FORM_NAME = 'sa-stage-goal';

type CreateGoalsFormData = {
    conditions: ConditionInput[];
    newScheduledActivityTypes: string[];
    newLoggedActivityTypes: string[];
};
type UpdateGoalsFormData = {
    conditions: ConditionInput[];
    newScheduledActivityTypes: string[];
    newLoggedActivityTypes: string[];
    id: string;
};

// These _values_ are type checked from the server, but its nice to have
// them in constants in case they change, and its slightly safer.
const LEAD_VALUE = 'LEAD_VALUE';
const BASIC_INFO = 'BASIC_INFO';
const ACTIVITY_LOGGED = 'ACTIVITY_LOGGED';
const LEAD_FIELDS = 'LEAD_FIELDS';
const EMAIL_RECEIVED = 'EMAIL_RECEIVED';
const EMAIL_SENT = 'EMAIL_SENT';
const EMAIL_CLICKED = 'EMAIL_CLICKED';
const ACTIVITY_SCHEDULED = 'ACTIVITY_SCHEDULED';
const QUOTE_SENT = 'QUOTE_SENT';
const QUOTE_ACCEPTED = 'QUOTE_ACCEPTED';
const INVOICE_SENT = 'INVOICE_SENT';
const INVOICE_PAID = 'INVOICE_PAID';

const DEFAULT_VALUES: GoalsFormData = {
    goalType: null,
    currencyValue: 0,
    currencyComparator: 'GREATER',
    hasRelated: {
        name: false,
        emailAddress: false,
        phone: false,
    },
    loggedActivityTypes: [null],
    scheduledActivityTypes: [null],
    leadFields: [null],
};

type OwnProps = {
    isNew: boolean;
    entityId: string | null | undefined;
    parentId: string;
    closeForm: () => void;
};

type GraphQlProps = {
    goal: SalesAutomationStageGoalFragment | null | undefined;
    isFetching: boolean;
    createGoal: (arg1: CreateGoalsFormData) => Promise<any>;
    updateGoal: (arg1: UpdateGoalsFormData) => Promise<any>;
    deleteGoal: (arg1: DeleteGoalInputFieldInput) => Promise<any>;
};
type Props = ConnectedProps & WithReduxForm & GraphQlProps;

/*
 * Wrapper component around our goals form that hooks up to GraphQL
 * and passes down the relevant goals (if they exist), and goal mutations:
 *
 * - CreateGoal
 *
 */
export class FormGoalsGraphQLComponent extends React.PureComponent<Props> {
    render() {
        return (
            <FormGoalsComponent
                handleSubmit={this.props.handleSubmit}
                loggedActivityTypes={this.props.loggableActivityTypes}
                scheduledActivityTypes={this.props.loggableActivityTypes}
                goal={this.props.goal}
                goalTypeSelected={this.props.goalTypeSelected}
                isFetchingLatestVersion={this.props.isFetching}
                isSubmitting={this.props.submitting}
                // @ts-expect-error
                isInvalid={this.props.anyTouched && this.props.invalid}
                // Global form validation error from react-redux, don't show until
                error={this.props.anyTouched ? this.props.error : undefined}
                onDelete={this.deleteForm}
                onClose={this.props.closeForm}
            />
        );
    }

    deleteForm = (entityId: string) => {
        return this.props.deleteGoal({id: entityId});
    };
}

// -----------------------------------------------------------------------------
//   React-redux connector
// -----------------------------------------------------------------------------

type StateProps = {
    loggableActivityTypes: ActivityType[];
    goalTypeSelected: GoalType;
};

type ConnectedProps = OwnProps &
    StateProps & {
        dispatch: Dispatch<any>;
    };

const formSelector = formValueSelector(FORM_NAME);

function mapStateToProps(state: NutshellState): StateProps {
    return {
        loggableActivityTypes: ActivityTypes.getAllLoggable(state),
        goalTypeSelected: formSelector(state, 'goalType'),
    };
}

// @ts-expect-error
const connector = connect<ConnectedProps, OwnProps, _, _, _, _>(mapStateToProps);

// -----------------------------------------------------------------------------
//   redux-form
// -----------------------------------------------------------------------------

type WithInitialValues = OwnProps &
    StateProps &
    GraphQlProps & {
        initialValues: GoalsFormData;
    };

/**
 * Takes an existing goal, gets its conditions, and seeds our state
 * with those conditions
 *
 * @param  {Object} goal                            - Existing goal object
 * @return {Object}                                 - Slice of state to set for this UI
 */
const transformConditionsToState = (goal: SalesAutomationStageGoalFragment) => {
    const filters = goal.conditions
        .filter((condition) => condition.config.__typename === 'ConditionConfigEntityFilter')
        .map((condition) => {
            // @ts-expect-error
            return condition.config.filter;
        });

    if (goal.goalType === LEAD_VALUE) {
        const currencyFilter = filters[0];

        // Ensure we're working with a currency filter here - the union
        // could be earlier, but our server isn't setup that way right now
        if (currencyFilter.__typename === 'EntityFilterFieldConfigCurrency') {
            return {
                // Technically this can be null, in the future-case that
                // passing null actually means "filter on `no value`"
                currencyValue: currencyFilter.currencyValue || 0,
                currencyComparator: currencyFilter.currencyComparator,
            };
        }
    } else if (goal.goalType === BASIC_INFO) {
        const appliedFilterNames = filters.map((filter) => {
            return filter.fieldName;
        });

        return {
            hasRelated: {
                name: appliedFilterNames.includes('relatedNames'),
                emailAddress: appliedFilterNames.includes('relatedEmailAddresses'),
                phone: appliedFilterNames.includes('phone'),
            },
        };
    } else if (goal.goalType === ACTIVITY_SCHEDULED) {
        const filter = filters[0];

        if (filter.__typename === 'EntityFilterFieldConfigRelationship') {
            return {
                scheduledActivityTypes: [...(filter.relationshipData || [])],
            };
        }
    } else if (goal.goalType === ACTIVITY_LOGGED) {
        const filter = filters[0];

        if (filter.__typename === 'EntityFilterFieldConfigRelationship') {
            return {
                loggedActivityTypes: [...(filter.relationshipData || [])],
            };
        }
    } else if (goal.goalType === LEAD_FIELDS) {
        const cleanedFilters = filters.map((filter) => {
            const copy = {...filter} as const;
            delete copy.__typename;

            return copy;
        });

        return {
            leadFields: [...(cleanedFilters || [])],
        };
    }
};

// @ts-expect-error
function initialValuesHOC(WrappedComponent) {
    return class extends React.PureComponent<OwnProps & StateProps & GraphQlProps> {
        render() {
            const goal = this.props.goal;
            const initialValues = goal
                ? {
                      ...DEFAULT_VALUES,
                      ...transformConditionsToState(goal),
                      goalType: goal.goalType,
                  }
                : DEFAULT_VALUES;

            return <WrappedComponent {...this.props} initialValues={initialValues} />;
        }
    };
}

type WithSubmissionProps = WithInitialValues & {
    onSubmit: (formData: GoalsFormData) => Promise<any>;
};

// @ts-expect-error
function submissionHOC(WrappedComponent): React.ComponentType<any> {
    return class extends React.PureComponent<
        WithInitialValues,
        {
            errorMessage: string | null | undefined;
        }
    > {
        constructor(props: WithInitialValues) {
            super(props);
            this.state = {
                errorMessage: null,
            };
        }

        render() {
            return (
                <WrappedComponent
                    {...this.props}
                    onSubmit={this.saveForm}
                    onSubmitSuccess={this.props.closeForm}
                    error={this.state.errorMessage}
                />
            );
        }

        saveForm = (formData: GoalsFormData) => {
            const conditions = getConditionsFromForm(formData);
            const newScheduledActivityTypes = getNewActivityTypeNames(
                formData.scheduledActivityTypes
            );
            const newLoggedActivityTypes = getNewActivityTypeNames(formData.loggedActivityTypes);

            if (
                conditions.length === 0 &&
                newScheduledActivityTypes.length === 0 &&
                newLoggedActivityTypes.length === 0
            ) {
                return Promise.reject('No valid conditions to save');
            }

            let promise;
            if (this.props.goal) {
                promise = this.props.updateGoal({
                    id: this.props.goal.id,
                    conditions,
                    newScheduledActivityTypes,
                    newLoggedActivityTypes,
                });
            } else {
                promise = this.props.createGoal({
                    conditions,
                    newScheduledActivityTypes,
                    newLoggedActivityTypes,
                });
            }

            return promise
                .catch((err) => {
                    const errorMessage = safelyGetGraphQLErrorMessage(err);
                    throw new SubmissionError({_error: errorMessage});
                })
                .then(() => {
                    if (newScheduledActivityTypes.length > 0 || newLoggedActivityTypes.length > 0) {
                        Nut.managedCollections.ActivityTypes.fetch();
                    }
                });
        };
    };
}

/**
 * Takes a filter and creates a condition with that filter value.
 *
 * @param  {Object} filterValue          - Filter value to create a condition
 *                                         from. Will always only be one filter
 * @return {Object}                      - Full condition object to pass
 *                                         to the server
 */
function createConditionFromFilter(filterValue: EntityFilterFieldConfigInput): ConditionInput {
    return {
        config: {
            filter: filterValue,
        },
    };
}

/*
 * Takes our existing state and turns that into API-consumable conditions
 * to pass to the server
 */
function getConditionsFromForm(formData: GoalsFormData): ConditionInput[] {
    if (formData.goalType === LEAD_VALUE && formData.currencyComparator) {
        const condition = createConditionFromFilter({
            fieldName: 'value',
            fieldType: 'CURRENCY',
            currencyConfig: {
                currencyComparator: formData.currencyComparator,
                currencyValue: formData.currencyValue,
                currencyCode: Session.getInstanceDefaultCurrencyCode(),
            },
        });

        return [condition];
    } else if (formData.goalType === BASIC_INFO) {
        const conditions = [];

        if (formData.hasRelated.name) {
            const condition = createConditionFromFilter({
                fieldName: 'relatedNames',
                fieldType: 'STRINGS',
                stringsConfig: {
                    stringsContain: '*',
                },
            });

            conditions.push(condition);
        }

        if (formData.hasRelated.emailAddress) {
            const condition = createConditionFromFilter({
                fieldName: 'relatedEmailAddresses',
                fieldType: 'STRINGS',
                stringsConfig: {
                    stringsContain: '*',
                },
            });

            conditions.push(condition);
        }

        if (formData.hasRelated.phone) {
            const condition = createConditionFromFilter({
                fieldName: 'phone',
                fieldType: 'PHONE',
                phoneConfig: {
                    startsWith: '*',
                },
            });

            conditions.push(condition);
        }

        return conditions;
    } else if (formData.goalType === ACTIVITY_SCHEDULED) {
        const conditions = [];

        const existingActivityTypeIds: string[] = formData.scheduledActivityTypes
            // filter through Boolean to remove void values https://stackoverflow.com/a/44131503
            .filter(Boolean)
            // @ts-expect-error
            .filter(isActivityTypeId);

        if (existingActivityTypeIds.length) {
            const condition = createConditionFromFilter({
                fieldName: 'activityTypes',
                fieldType: 'RELATIONSHIP',
                relationshipConfig: {
                    relationshipAnyAll: 'ANY',
                    relationshipData: existingActivityTypeIds,
                },
            });

            conditions.push(condition);
        }

        return conditions;
    } else if (formData.goalType === ACTIVITY_LOGGED) {
        const conditions = [];

        const existingActivityTypeIds: string[] = formData.loggedActivityTypes
            // filter through Boolean to remove void values https://stackoverflow.com/a/44131503
            .filter(Boolean)
            // @ts-expect-error
            .filter(isActivityTypeId);

        if (existingActivityTypeIds.length) {
            const condition = createConditionFromFilter({
                fieldName: 'loggedActivityTypes',
                fieldType: 'RELATIONSHIP',
                relationshipConfig: {
                    relationshipAnyAll: 'ANY',
                    relationshipData: existingActivityTypeIds,
                },
            });

            conditions.push(condition);
        }

        return conditions;
    } else if (formData.goalType === LEAD_FIELDS) {
        // @ts-expect-error
        return (
            formData.leadFields
                .filter(Boolean)
                .filter(filterInvalidConditionLeadFieldData)
                .map(createConditionFromLeadFieldFilter)
                // Filter invalid (undefined) condition lead field mappings
                .filter(Boolean)
        );
    } else if (formData.goalType === EMAIL_RECEIVED) {
        const condition = createConditionFromFilter({
            fieldName: 'emailReceived',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    } else if (formData.goalType === EMAIL_SENT) {
        const condition = createConditionFromFilter({
            fieldName: 'emailSent',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    } else if (formData.goalType === EMAIL_CLICKED) {
        const condition = createConditionFromFilter({
            fieldName: 'emailClicked',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    } else if (formData.goalType === QUOTE_SENT) {
        const condition = createConditionFromFilter({
            fieldName: 'quoteSent',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    } else if (formData.goalType === QUOTE_ACCEPTED) {
        const condition = createConditionFromFilter({
            fieldName: 'quoteAccepted',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    } else if (formData.goalType === INVOICE_SENT) {
        const condition = createConditionFromFilter({
            fieldName: 'invoiceSent',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    } else if (formData.goalType === INVOICE_PAID) {
        const condition = createConditionFromFilter({
            fieldName: 'invoicePaid',
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: true,
            },
        });

        return [condition];
    }

    return [];
}

// @ts-expect-error
function filterInvalidConditionLeadFieldData(condition) {
    return (
        // If it's an `isSetValue` config, we don't care if there's a value
        // or not, so we just send it along
        condition.isSetValue ||
        // Enum values need to have a choice selected.
        (condition.fieldType === 'ENUM' && condition.enumValue) ||
        (condition.fieldName === 'value' && condition.currencyComparator && condition.currencyValue)
    );
}

// @ts-expect-error
function createConditionFromLeadFieldFilter(leadFieldCondition) {
    if (leadFieldCondition.isSetValue) {
        return createConditionFromFilter({
            fieldName: leadFieldCondition.fieldName,
            fieldType: leadFieldCondition.fieldType,
            isSetConfig: {
                isSetValue: leadFieldCondition.isSetValue,
            },
        });
        // Right now, this is just enumValue
    } else if (leadFieldCondition.enumValue) {
        return createConditionFromFilter({
            fieldName: leadFieldCondition.fieldName,
            fieldType: leadFieldCondition.fieldType,
            enumConfig: {
                enumValue: leadFieldCondition.enumValue,
                enumIsNot: false,
            },
        });
    } else if (leadFieldCondition.currencyValue) {
        return createConditionFromFilter({
            fieldName: 'value',
            fieldType: 'CURRENCY',
            currencyConfig: {
                currencyComparator: leadFieldCondition.currencyComparator,
                currencyValue: leadFieldCondition.currencyValue,
                currencyCode: Session.getInstanceDefaultCurrencyCode(),
            },
        });
    }
}

function getNewActivityTypeNames(activityTypes: Array<string | null | undefined>): string[] {
    const newActivityTypes: string[] = activityTypes
        // filter through Boolean to remove void values https://stackoverflow.com/a/44131503
        .filter(Boolean)
        // @ts-expect-error
        .filter(isNotActivityTypeId);

    return newActivityTypes;
}

function validateGoalsForm(formData: GoalsFormData) {
    if (!formData.goalType) {
        return {
            _error: 'Please choose a goal type',
        };
    }

    if (
        formData.goalType === 'BASIC_INFO' &&
        !formData.hasRelated.name &&
        !formData.hasRelated.emailAddress &&
        !formData.hasRelated.phone
    ) {
        return {
            hasRelated: 'You must choose at least one option',
        };
    }

    if (
        formData.goalType === 'ACTIVITY_SCHEDULED' &&
        (!formData.scheduledActivityTypes ||
            !formData.scheduledActivityTypes.length ||
            // We seed `null` into the first field so that we have a picker to choose from
            (formData.scheduledActivityTypes.length === 1 &&
                formData.scheduledActivityTypes[0] === null))
    ) {
        return {
            scheduledActivityTypes: {_error: 'Choose an activity type for this goal'},
        };
    }

    if (
        formData.goalType === 'ACTIVITY_LOGGED' &&
        (!formData.loggedActivityTypes ||
            !formData.loggedActivityTypes.length ||
            // We seed `null` into the first field so that we have a picker to choose from
            (formData.loggedActivityTypes.length === 1 && formData.loggedActivityTypes[0] === null))
    ) {
        return {
            loggedActivityTypes: {_error: 'Choose an activity type for this goal'},
        };
    }
}

type WithReduxForm = WithSubmissionProps & FormProps;

const withReduxForm = compose(
    initialValuesHOC,
    submissionHOC,
    reduxForm({
        form: FORM_NAME,
        // @ts-expect-error
        validate: validateGoalsForm,
        enableReinitialize: true, // It takes a second or two for the `goal` to be fetched
        // onSubmitSuccess: (result: Object, dispatch: Dispatch) => { dispatch(clearActiveItem()); },
    })
);

const ReduxFormedGoalsComponent = withReduxForm(FormGoalsGraphQLComponent);

// -----------------------------------------------------------------------------
//   graphql
// -----------------------------------------------------------------------------

class GoalForStageQuery extends Query<GetGoalQuery, GetGoalQueryVariables> {}
class CreateMutation extends Mutation<CreateGoalMutation, CreateGoalMutationVariables> {}
class UpdateMutation extends Mutation<UpdateGoalMutation, UpdateGoalMutationVariables> {}
class DeleteMutation extends Mutation<DeleteGoalMutation, DeleteGoalMutationVariables> {}

const FormGoalsGraphQL = (props: OwnProps & StateProps) => {
    return (
        <GoalForStageQuery
            query={GET_GOAL}
            // @ts-expect-error
            fetchPolicy='cache-and-network'
            skip={!props.entityId}
            variables={{id: props.entityId || ''}}
        >
            {({data, loading}) => {
                const goal = data ? data.goal : null;

                return (
                    <CreateMutation
                        mutation={CREATE_GOAL}
                        // @ts-expect-error
                        refetchQueries={refetchSalesAutomationOpenLeadQueries({
                            stageId: props.parentId,
                        })}
                        update={(proxy, mutationResult) => {
                            const createGoal =
                                mutationResult.data && mutationResult.data.createGoal;
                            if (!createGoal) return;

                            // Read the data from our cache for this query.
                            const cacheResult: StageGoalsForStageQuery | null | undefined =
                                proxy.readQuery({
                                    query: GET_STAGE_GOALS_FOR_STAGE,
                                    variables: {stageId: props.parentId},
                                });
                            if (!cacheResult) return;

                            // Set our goals to the new goal. This works becuase
                            // we only allow a single goal
                            cacheResult.editableGoals = [createGoal.goal];

                            // Write our data back to the cache.
                            proxy.writeQuery({
                                query: GET_STAGE_GOALS_FOR_STAGE,
                                variables: {stageId: props.parentId},
                                data: cacheResult,
                            });
                        }}
                    >
                        {(createGoal) => (
                            <UpdateMutation
                                mutation={UPDATE_GOAL}
                                // @ts-expect-error
                                refetchQueries={refetchSalesAutomationOpenLeadQueries({
                                    stageId: props.parentId,
                                })}
                                update={(proxy, mutationResult) => {
                                    const updatedGoal =
                                        mutationResult.data && mutationResult.data.updateGoal.goal;
                                    if (!updatedGoal) return;

                                    // Read the data from our cache for this query.
                                    const cacheResult: StageGoalsForStageQuery | null | undefined =
                                        proxy.readQuery({
                                            query: GET_STAGE_GOALS_FOR_STAGE,
                                            variables: {stageId: props.parentId},
                                        });
                                    if (!cacheResult) return;

                                    // Set our goals to the updated goal. This works becuase
                                    // we only allow a single goal
                                    cacheResult.editableGoals = [updatedGoal];

                                    // Write our data back to the cache.
                                    proxy.writeQuery({
                                        query: GET_STAGE_GOALS_FOR_STAGE,
                                        variables: {stageId: props.parentId},
                                        data: cacheResult,
                                    });
                                }}
                            >
                                {(updateGoal) => (
                                    <DeleteMutation
                                        mutation={DELETE_GOAL}
                                        // @ts-expect-error
                                        refetchQueries={refetchSalesAutomationOpenLeadQueries({
                                            stageId: props.parentId,
                                        })}
                                        update={(proxy) => {
                                            // Read the data from our cache for this query.
                                            const cacheResult:
                                                | StageGoalsForStageQuery
                                                | null
                                                | undefined = nutApolloClient.readQuery({
                                                query: GET_STAGE_GOALS_FOR_STAGE,
                                                variables: {stageId: props.parentId},
                                            });
                                            if (!cacheResult) return;

                                            cacheResult.editableGoals = [];

                                            // Write our empty goal data back to the cache.
                                            proxy.writeQuery({
                                                query: GET_STAGE_GOALS_FOR_STAGE,
                                                variables: {
                                                    stageId: props.parentId,
                                                },
                                                data: cacheResult,
                                            });
                                        }}
                                    >
                                        {(deleteGoal) => (
                                            <ReduxFormedGoalsComponent
                                                {...props}
                                                goal={goal}
                                                isFetching={loading}
                                                createGoal={(formData) =>
                                                    createGoal({
                                                        variables: {
                                                            goal: {
                                                                stageId: props.parentId,
                                                                ...formData,
                                                            },
                                                        },
                                                    })
                                                }
                                                updateGoal={(formData) =>
                                                    updateGoal({
                                                        variables: {goal: {...formData}},
                                                    })
                                                }
                                                deleteGoal={(formData) => {
                                                    return deleteGoal({
                                                        variables: {
                                                            goal: formData,
                                                        },
                                                    });
                                                }}
                                            />
                                        )}
                                    </DeleteMutation>
                                )}
                            </UpdateMutation>
                        )}
                    </CreateMutation>
                );
            }}
        </GoalForStageQuery>
    );
};

export const FormGoals = connector(FormGoalsGraphQL);
